import type { AxiosResponse } from "axios"
import type {
  AccessPolicyVersion,
  AddPolicyVersionRequest,
  ListedAccessPolicy,
  SetPolicyEnabledRequest,
  StoredPolicy
} from "@/types/access-policy"
import type { CreateApiKeyRequest } from "@/stores/api_key"

/**
 * @description Manage entities related to S3
 */
class S3ApiService {
  /**
   * @description Service base URL (without trailing `/`)
   */
  public static base_url: string = import.meta.env.VITE_APP_S3_API_SERVICE_URL
  public static gateway_base_url: string = import.meta.env.VITE_APP_S3_GATEWAY_URL


  public static axios: any

  public static init (axiosInstance: any) {
    S3ApiService.axios = axiosInstance
  }

  public static list_apikeys(include_access_policy: boolean = false) {
    const url_params = {
      'include_access_policy': Number(include_access_policy)
    }
    return S3ApiService.axios.get(S3ApiService.base_url+'/apikey', { params: url_params })
  }

  public static insert_apikey(payload: CreateApiKeyRequest) {
    return S3ApiService.axios.post(S3ApiService.base_url+'/apikey', payload)
  }

  public static delete_apikey(access_key: string) {
    const url_params = {
      'access_key': access_key
    }
    return S3ApiService.axios.delete(S3ApiService.base_url+'/apikey', { params: url_params })
  }

  public static enable_disable_apikey(payload: object) {
    return S3ApiService.axios.put(`${S3ApiService.base_url}/apikey/disable`, payload)
  }

  public static list_buckets(with_object_count: boolean | null = null) {
    const url_params = {}
    if(with_object_count !== null) url_params['with_object_count'] = Number(with_object_count)
    return S3ApiService.axios.get(`${S3ApiService.base_url}/bucket`, { params: url_params })
  }

  public static create_bucket(payload: object) {
    return S3ApiService.axios.post(S3ApiService.base_url+'/bucket', payload)
  }
  public static delete_bucket(bucket_name: string) {
    return S3ApiService.axios.delete(`${S3ApiService.base_url}/bucket/${bucket_name}`)
  }

  public static list_bucket_objects = (bucket_name: string, continuationToken: string = '', prefix: string = '', delimiter: string|null = null) => {
    const url_params = {}
    if(continuationToken.length) url_params['continuation-token'] = continuationToken
    if(prefix.length) url_params['prefix'] = prefix
    if(delimiter !== null) url_params['delimiter'] = delimiter

    return S3ApiService.axios.get(`${S3ApiService.base_url}/bucket/${bucket_name}/objects`, { params: url_params })
  }

  public static list_bucket_versions = (bucket_name: string, key_marker: string = '', version_id_marker: string = '', prefix: string = '', delimiter: string|null = null) => {
    const url_params = {}
    if(key_marker.length) url_params['key-marker'] = key_marker
    if(version_id_marker.length) url_params['version-id-marker'] = version_id_marker
    if(prefix.length) url_params['prefix'] = prefix
    if(delimiter !== null) url_params['delimiter'] = delimiter

    return S3ApiService.axios.get(`${S3ApiService.base_url}/bucket/${bucket_name}/versions`, { params: url_params })
  }

  public static delete_object(bucket_name: string, object_name: string) {
    // Creates a Delete Marker
    return S3ApiService.axios.delete(`${S3ApiService.base_url}/bucket/${bucket_name}/${object_name}`)
  }

  public static delete_object_version(bucket_name: string, object_name: string, version_id: string) {
    // Permanently deletes the object version
    const url_params = {
      'versionId': version_id
    }
    return S3ApiService.axios.delete(`${S3ApiService.base_url}/bucket/${bucket_name}/${object_name}`, { params: url_params })
  }

  public static is_bucket_name_used(bucket_name: string) {
    const url_params = {
      'name': bucket_name
    }
    return S3ApiService.axios.get(`${S3ApiService.base_url}/bucket/is_used`, { params: url_params })
  }

  public static download_object(bucket_name: string, object_name: string) {
    return S3ApiService.axios.get(`${S3ApiService.gateway_base_url}/${bucket_name}/${object_name}`, {responseType: 'blob'})
  }

  public static list_bucket_downloads = (bucket_name: string, offset: number=0, howmany: number=100, include_object_names: boolean = false) => {
    const url_params = {
      'offset': offset,
      'howmany': howmany,
      'with_object_names': Number(include_object_names)
    }
    return S3ApiService.axios.get(`${S3ApiService.base_url}/bucket/${bucket_name}/downloads`, { params: url_params })
  }

  public static get_usage_event_metrics = (event_type: string, usage_event_id: number) => {
    const url_params = {
      'event_type': event_type,
      'usage_event_id': usage_event_id
    }
    return S3ApiService.axios.get(`${S3ApiService.base_url}/transfer_metrics/get_by_usage_event_id`, { params: url_params })
  }

  /**
   * List download metrics for a namespace with optional time/region filters.
   * @param namespace_id Bucket namespace ID.
   * @param from_unixtime Inclusive start time (Unix seconds) or null.
   * @param to_unixtime Exclusive end time (Unix seconds) or null.
   * @param fly_region Optional Fly.io region filter.
   * @param cloud_only When true, include only Fly-hosted downloads.
   * @param return_fields Optional list of fields to return.
   * @returns List of download metric records.
   */
  public static list_download_event_metrics = (
    namespace_id: number,
    usage_event_ids: number[] | null = null,
    from_unixtime: number | null = null,
    to_unixtime: number | null = null,
    fly_region: string | null = null,
    cloud_only: boolean = true,
    return_fields: string[] | null = null
  ) => {
    const payload = {
      'namespace_id': namespace_id,
      'cloud_only': Number(cloud_only)
    }
    if (usage_event_ids !== null && usage_event_ids.length) payload['usage_event_ids'] = usage_event_ids
    if (from_unixtime !== null) payload['from_unixtime'] = from_unixtime
    if (to_unixtime !== null) payload['to_unixtime'] = to_unixtime
    if (fly_region !== null) payload['fly_region'] = fly_region
    if (return_fields !== null && return_fields.length) payload['return_fields'] = return_fields

    return S3ApiService.axios.post(`${S3ApiService.base_url}/transfer_metrics/list_download_metrics`, payload)
  }

  public static change_download_strategy = (bucket_name: string, download_strategy: string) => {
    const payload = {
      'download_strategy': download_strategy
    }
    return S3ApiService.axios.put(`${S3ApiService.base_url}/bucket/${bucket_name}/download_strategy`, payload)
  }

  public static list_access_policies(): Promise<AxiosResponse<ListedAccessPolicy[]>> {
    return S3ApiService.axios.get(S3ApiService.base_url+'/access-policy')
  }

  public static get_access_policy(policy_id: string): Promise<AxiosResponse<ListedAccessPolicy>> {
    return S3ApiService.axios.get(`${S3ApiService.base_url}/access-policy/${policy_id}`)
  }

  public static list_access_policy_versions(policy_id: string): Promise<AxiosResponse<AccessPolicyVersion[]>> {
    return S3ApiService.axios.get(`${S3ApiService.base_url}/access-policy/${policy_id}/versions`)
  }

  public static add_access_policy_version(
    policy_id: string,
    payload: AddPolicyVersionRequest
  ): Promise<AxiosResponse<StoredPolicy>> {
    return S3ApiService.axios.post(
      `${S3ApiService.base_url}/access-policy/${policy_id}/versions`,
      payload
    )
  }

  public static get_access_policy_version(
    policy_id: string,
    version: number
  ): Promise<AxiosResponse<AccessPolicyVersion>> {
    return S3ApiService.axios.get(
      `${S3ApiService.base_url}/access-policy/${policy_id}/versions/${version}`
    )
  }

  public static set_access_policy_enabled(
    policy_id: string,
    payload: SetPolicyEnabledRequest
  ): Promise<AxiosResponse<StoredPolicy>> {
    return S3ApiService.axios.patch(
      `${S3ApiService.base_url}/access-policy/${policy_id}/enabled`,
      payload
    )
  }

}

export default S3ApiService
